/**
 * slide-state-manager
 * manages slide DOM state including classes, CSS custom properties, and ARIA attributes
 * - called directly by frame-engine at the end of the render pipeline
 * - updates slide visibility classes, selection state, and animation CSS variables
 * - handles accessibility attributes and keyboard navigation state
 * - coordinates slide state based on viewport position and carousel interactions
 */
export default class SlideStateManager {
	/** @type {object} shared module context */
	ctx;

	/**
	 * @constructor
	 * @param {object} ctx - shared module context
	 * @param {object} ctx.emitter
	 * @param {object} ctx.events
	 * @param {HTMLElement} ctx.carousel
	 * @param {HTMLElement} ctx.viewport
	 * @param {import('../core/data-store.js').default} ctx.store
	 */
	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;

		// cache for visibility info to avoid recalculating for class application
		_.#lastVisibilityInfo = new Map();

		// bind event handlers
		_.handlers = {
			renderIndexChanged: ({ currentIndex }) => {
				_.handleSlideNavigation(currentIndex);
			},
			visibilityChanged: ({ partiallyVisibleSlides, mostlyVisibleSlides, fullyVisibleSlides }) => {
				_.applyVisibilityClasses(partiallyVisibleSlides, mostlyVisibleSlides, fullyVisibleSlides);
			},
			optionsChanged: () => _.syncRenderMetrics(),
		};

		_.init();
	}

	/** @type {Map<HTMLElement, object>} cached visibility info per slide */
	#lastVisibilityInfo;

	/** @type {boolean} whether per-frame slide metric CSS vars are written (cached from options) */
	#renderMetrics = false;

	/**
	 * initialize the slide state manager
	 */
	init() {
		const _ = this;
		_.#renderMetrics = !!_.ctx.store.getOptions().renderSlideMetrics;
		_.bindEvents();
	}

	/**
	 * bind event listeners
	 */
	bindEvents() {
		const _ = this;
		// listen for slide navigation events (renderIndex changes during normal navigation)
		_.ctx.emitter.on(_.ctx.events.store.renderIndexChanged, _.handlers.renderIndexChanged);
		// listen for visibility changes to apply classes/ARIA (event-driven, not per-frame)
		_.ctx.emitter.on(_.ctx.events.slides.visibleChanged, _.handlers.visibilityChanged);
		// listen for options changes to toggle per-frame metric CSS vars
		_.ctx.emitter.on(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
	}

	/**
	 * syncs the cached renderSlideMetrics flag from options; clears stale
	 * metric CSS vars from all slides when the option is turned off
	 */
	syncRenderMetrics() {
		const _ = this;
		const enabled = !!_.ctx.store.getOptions().renderSlideMetrics;
		const wasEnabled = _.#renderMetrics;
		_.#renderMetrics = enabled;

		// if just disabled, remove any stale metric vars left on slides
		if (wasEnabled && !enabled) {
			const slides = _.ctx.carousel.querySelectorAll('tarot-slide');
			for (const slide of slides) {
				_.#clearSlideMetricProps(slide);
			}
		}
	}

	/**
	 * removes the per-frame metric CSS custom properties from a slide
	 * @param {HTMLElement} slide - the slide element
	 */
	#clearSlideMetricProps(slide) {
		slide.style.removeProperty('--tarot-visibility');
		slide.style.removeProperty('--tarot-left-visibility');
		slide.style.removeProperty('--tarot-right-visibility');
		slide.style.removeProperty('--tarot-parallax');
		slide.style.removeProperty('--tarot-parallax-visibility');
	}

	/**
	 * updates visibility data per-frame: CSS custom properties and store
	 * classes/ARIA are applied separately via event subscription (not per-frame)
	 * @param {number|null} [trackPosition=null] - optional track position override
	 */
	updateSlides(trackPosition = null) {
		const _ = this;

		// read current track position from the store if not provided
		if (trackPosition === null) {
			trackPosition = _.ctx.store.getAnimation().trackPosition || 0;
		}

		// update selection state on ALL slides (not just visible ones)
		_.updateAllSlidesSelection();

		// collect the current set of slide elements using ctx data store
		const slideInfos = _.ctx.utils.getSlidesInViewport(_.ctx, trackPosition);

		// track slides by visibility level
		const partiallyVisibleSlides = [];
		const mostlyVisibleSlides = [];
		const fullyVisibleSlides = [];

		for (const info of slideInfos) {
			// update CSS custom properties (per-frame for smooth animations)
			_.updateSlideProperties(info);

			// cache visibility info for class application
			_.#lastVisibilityInfo.set(info.slide, info);

			// collect slides by visibility level for the data store
			if (info.isPartiallyVisible) {
				partiallyVisibleSlides.push(info.slide);
				if (info.isMostlyVisible) {
					mostlyVisibleSlides.push(info.slide);
					if (info.isFullyVisible) {
						fullyVisibleSlides.push(info.slide);
					}
				}
			}
		}

		// update visibility in the data store (only emits event if changed)
		_.ctx.store.setVisibility({ partiallyVisibleSlides, mostlyVisibleSlides, fullyVisibleSlides });
	}

	/**
	 * applies visibility classes and ARIA attributes (event-driven, not per-frame)
	 * @param {HTMLElement[]} partiallyVisibleSlides - slides with any visibility (> 0%)
	 * @param {HTMLElement[]} mostlyVisibleSlides - slides with >= 66% visibility
	 * @param {HTMLElement[]} fullyVisibleSlides - slides with >= 98% visibility
	 */
	applyVisibilityClasses(partiallyVisibleSlides, mostlyVisibleSlides, fullyVisibleSlides) {
		const _ = this;
		const allSlides = _.ctx.store.getSlides();
		const partiallyVisibleSet = new Set(partiallyVisibleSlides);
		const mostlyVisibleSet = new Set(mostlyVisibleSlides);
		const fullyVisibleSet = new Set(fullyVisibleSlides);

		for (const slide of allSlides) {
			const isPartiallyVisible = partiallyVisibleSet.has(slide);
			const isMostlyVisible = mostlyVisibleSet.has(slide);
			const isFullyVisible = fullyVisibleSet.has(slide);
			const info = _.#lastVisibilityInfo.get(slide);
			const visibilityPercent = info?.visibilityPercent ?? 0;

			// remove all visibility classes first
			slide.classList.remove('tarot-hidden', 'tarot-partially-visible', 'tarot-mostly-visible', 'tarot-fully-visible');

			// add only the most specific visibility class (mutually exclusive)
			if (isFullyVisible) {
				slide.classList.add('tarot-fully-visible');
			} else if (isMostlyVisible) {
				slide.classList.add('tarot-mostly-visible');
			} else if (isPartiallyVisible) {
				slide.classList.add('tarot-partially-visible');
			} else {
				slide.classList.add('tarot-hidden');
			}

			// update ARIA and inert (use mostly visible for accessibility - 66%+)
			// content should be meaningfully visible before being accessible
			_.updateSlideARIA(slide, isMostlyVisible, visibilityPercent);
			slide.inert = !isMostlyVisible;
		}
	}

	/**
	 * updates CSS custom properties for a slide (per-frame for smooth animations)
	 * @param {{ slide:HTMLElement, visibilityPercent:number, leftVisibility:number, rightVisibility:number, parallax:number, parallaxVisibility:number }} slideInfo
	 */
	updateSlideProperties(slideInfo) {
		const { slide, visibilityPercent, leftVisibility, rightVisibility, parallax, parallaxVisibility } = slideInfo;

		// always set: read by the adaptive-height plugin (slide._visibility)
		slide._visibility = visibilityPercent;

		// opt-in per-frame DOM writes for CSS-driven styling (default off)
		if (!this.#renderMetrics) return;

		slide.style.setProperty('--tarot-visibility', visibilityPercent.toFixed(3));
		slide.style.setProperty('--tarot-left-visibility', leftVisibility.toFixed(3));
		slide.style.setProperty('--tarot-right-visibility', rightVisibility.toFixed(3));
		slide.style.setProperty('--tarot-parallax', parallax.toFixed(3));
		slide.style.setProperty('--tarot-parallax-visibility', parallaxVisibility.toFixed(3));
	}

	/**
	 * updates selection state (tarot-selected class) on all slides
	 */
	updateAllSlidesSelection() {
		const _ = this;
		const allSlides = _.ctx.store.getSlides();

		for (const slide of allSlides) {
			if (slide._selected) {
				slide.classList.add('tarot-selected');
			} else {
				slide.classList.remove('tarot-selected');
			}
		}
	}

	/**
	 * updates aria attributes for a slide
	 * @param {HTMLElement} slide - the slide element
	 * @param {boolean} isVisible - whether the slide is visible in viewport
	 * @param {number} visibilityPercent - percentage of slide visible (0..1)
	 */
	updateSlideARIA(slide, isVisible, visibilityPercent) {
		const _ = this;

		// mark offscreen content for assistive tech
		slide.setAttribute('aria-hidden', String(!isVisible));

		// build an aria-label if not present
		if (!slide.hasAttribute('aria-label') && !slide.hasAttribute('aria-labelledby')) {
			const indexAttr = slide.getAttribute('index') || '0';
			const index = parseInt(indexAttr, 10) || 0;
			const total = _.ctx.store.getState().slideCount || _.ctx.store.getSlides().length || 0;
			slide.setAttribute('aria-label', `${index + 1} of ${total}`);
		}

		// aria-live removed - now handled by dedicated announcement region in tarot.js

		// set aria-current on the slide that matches the current render index if author provided a mapping
		const renderIndex = _.ctx.store.getState().renderIndex;
		const slideIndexAttr = slide.getAttribute('index');
		if (slideIndexAttr != null && Number(slideIndexAttr) === renderIndex) {
			slide.setAttribute('aria-current', 'true');
		} else {
			slide.removeAttribute('aria-current');
		}
	}

	/**
	 * handles slide navigation announcements for screen readers
	 * @param {number} slideIndex - the newly selected slide index
	 */
	handleSlideNavigation(slideIndex) {
		const _ = this;
		const options = _.ctx.store.getOptions();

		// check if announcements are enabled
		if (!options.announcements || !_.ctx.announcements) return;

		const slides = _.ctx.store.getSlides();
		const total = slides.length;

		// create announcement text
		const announcement = `Slide ${slideIndex + 1} of ${total}`;

		// update announcement element content
		_.ctx.announcements.textContent = announcement;
	}

	/** cleanup: unbind events and reset slide state */
	destroy() {
		const _ = this;

		// unbind event handlers
		if (_.handlers?.renderIndexChanged) {
			_.ctx.emitter.off(_.ctx.events.store.renderIndexChanged, _.handlers.renderIndexChanged);
		}
		if (_.handlers?.visibilityChanged) {
			_.ctx.emitter.off(_.ctx.events.slides.visibleChanged, _.handlers.visibilityChanged);
		}
		if (_.handlers?.optionsChanged) {
			_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		}

		// clear visibility cache
		_.#lastVisibilityInfo?.clear();

		// reset slide state
		const slides = _.ctx.carousel.querySelectorAll('tarot-slide');
		for (const slide of slides) {
			slide.classList.remove(
				'tarot-hidden',
				'tarot-partially-visible',
				'tarot-mostly-visible',
				'tarot-fully-visible',
				'tarot-selected'
			);
			_.#clearSlideMetricProps(slide);
			slide.removeAttribute('aria-hidden');
			slide.removeAttribute('aria-current');
			slide.removeAttribute('aria-roledescription');
			slide.inert = false;
		}
	}
}
